class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const sections = FacetFiltersForm.getSections();
    if (!sections[0]?.section) {
      console.warn('[facets] Missing #product-grid[data-id]; cannot refresh collection.');
      return;
    }
    FacetFiltersForm.searchParamsPrev = searchParams;
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const sid = encodeURIComponent(section.section);
      const url =
        searchParams && searchParams.length > 0
          ? `${window.location.pathname}?section_id=${sid}&${searchParams}`
          : `${window.location.pathname}?section_id=${sid}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static unwrapSectionResponse(responseText) {
    const t = responseText.trim();
    if (t.startsWith('{')) {
      try {
        const data = JSON.parse(t);
        if (data.sections && typeof data.sections === 'object') {
          const first = Object.values(data.sections)[0];
          if (typeof first === 'string') return first;
        }
      } catch (e) {
        /* response is HTML, or non-JSON starting with { */
      }
    }
    return responseText;
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = FacetFiltersForm.unwrapSectionResponse(responseText);
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        try {
          FacetFiltersForm.renderFilters(html, event);
        } catch (err) {
          console.error('[facets] renderFilters failed', err);
        } finally {
          FacetFiltersForm.initPriceRangeSliders();
        }
        try {
          if (typeof initializeScrollAnimationTrigger === 'function') {
            initializeScrollAnimationTrigger(responseText);
          }
        } catch (err) {
          console.error('[facets] initializeScrollAnimationTrigger failed', err);
        }
      })
      .catch(() => {
        const grid = document.getElementById('ProductGridContainer');
        if (grid) {
          grid.querySelector('.collection')?.classList.remove('loading');
        }
        document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner').forEach((spinner) => {
          spinner.classList.add('hidden');
        });
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    try {
      FacetFiltersForm.renderFilters(html, event);
    } catch (err) {
      console.error('[facets] renderFilters failed', err);
    } finally {
      FacetFiltersForm.initPriceRangeSliders();
    }
    try {
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(html);
      }
    } catch (err) {
      console.error('[facets] initializeScrollAnimationTrigger failed', err);
    }
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sourceGrid = parsed.getElementById('ProductGridContainer');
    const targetGrid = document.getElementById('ProductGridContainer');
    if (!sourceGrid || !targetGrid) return;

    targetGrid.innerHTML = sourceGrid.innerHTML;

    targetGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsed.getElementById('ProductCount');
    const count = countEl ? countEl.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  /** Desktop + mobile facet rows share the same id (Details-N-sectionId); getElementById only ever returns desktop. */
  static getFacetFormRootFromElement(el) {
    if (!el || typeof el.closest !== 'function') return null;
    if (el.closest('#FacetFiltersForm')) return '#FacetFiltersForm';
    if (el.closest('#FacetFiltersFormMobile')) return '#FacetFiltersFormMobile';
    if (el.closest('#FacetFiltersPillsForm')) return '#FacetFiltersPillsForm';
    return null;
  }

  static escapeIdForSelector(id) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(id);
    }
    return String(id).replace(/([.#:[\],])/g, '\\$1');
  }

  static resolveFacetDetailsInDocument(elementFromFetch) {
    const root = FacetFiltersForm.getFacetFormRootFromElement(elementFromFetch);
    if (!root || !elementFromFetch.id) {
      return document.getElementById(elementFromFetch.id);
    }
    return document.querySelector(`${root} #${FacetFiltersForm.escapeIdForSelector(elementFromFetch.id)}`);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    const fetchKeys = () =>
      Array.from(facetDetailsElementsFromFetch).map((el) => ({
        root: FacetFiltersForm.getFacetFormRootFromElement(el),
        id: el.id,
      }));

    // Remove facets that are no longer returned from the server (match by form + id)
    const incoming = fetchKeys();
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      const root = FacetFiltersForm.getFacetFormRootFromElement(currentElement);
      const stillThere = incoming.some((k) => k.root === root && k.id === currentElement.id);
      if (!stillThere) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };
    const clickedRoot = event ? FacetFiltersForm.getFacetFormRootFromElement(event.target.closest('.js-filter')) : null;
    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      (element) =>
        matchesId(element) &&
        (!clickedRoot || FacetFiltersForm.getFacetFormRootFromElement(element) === clickedRoot)
    );

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = FacetFiltersForm.resolveFacetDetailsInDocument(elementToRender);
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName } = facetsToRender[index - 1];
          const previousFromFetch = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            const prevEl = FacetFiltersForm.resolveFacetDetailsInDocument(previousFromFetch);
            if (prevEl) {
              prevEl.after(elementToRender);
            }
            return;
          }
        }

        if (elementToRender.parentElement && elementToRender.parentElement.id) {
          const root = FacetFiltersForm.getFacetFormRootFromElement(elementToRender);
          const parentEl =
            root && document.querySelector(`${root} #${FacetFiltersForm.escapeIdForSelector(elementToRender.parentElement.id)}`);
          const firstFilter = parentEl && parentEl.querySelector('.js-filter');
          if (firstFilter) {
            firstFilter.before(elementToRender);
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event) {
      const clickedJsFilter = event.target.closest('.js-filter');
      if (clickedJsFilter && clickedJsFilter.id) {
        FacetFiltersForm.renderCounts(countsToRender, clickedJsFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, clickedJsFilter);

        const newElementSelector = clickedJsFilter.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = clickedJsFilter.querySelector(newElementSelector);
        if (newElementToActivate) newElementToActivate.focus();
      }
    }
  }

  /**
   * Binds native range inputs under .price-range-filter to the number inputs in .facets__price.
   * Inline scripts in Liquid do not run when facet HTML is swapped via AJAX; this must run after each update.
   */
  static initPriceRangeSliders() {
    document.querySelectorAll('#FacetFiltersForm .range-slider').forEach((details) => {
      const rangeWrap = details.querySelector('.price-range-filter');
      const numberWrap = details.querySelector('.facets__price');
      if (!rangeWrap || !numberWrap) return;

      const rangeS = rangeWrap.querySelectorAll('input[type="range"]');
      const numberS = numberWrap.querySelectorAll('input[type="number"]');
      if (rangeS.length < 2 || numberS.length < 2) return;

      const max2 = parseFloat(rangeS[1].getAttribute('max')) || 0;

      const syncRangeToNumbers = () => {
        let slide1 = parseFloat(rangeS[0].value);
        let slide2 = parseFloat(rangeS[1].value);
        if (Number.isNaN(slide1)) slide1 = 0;
        if (Number.isNaN(slide2)) slide2 = max2;
        if (slide1 > slide2) {
          [slide1, slide2] = [slide2, slide1];
          rangeS[0].value = String(slide1);
          rangeS[1].value = String(slide2);
        }
        numberS[0].value = slide1;
        numberS[1].value = slide2;
      };

      const syncNumbersToRange = () => {
        let n1 = parseFloat(numberS[0].value);
        let n2 = parseFloat(numberS[1].value);
        if (Number.isNaN(n1)) n1 = 0;
        if (Number.isNaN(n2)) n2 = max2;
        if (n1 > n2) {
          numberS[0].value = n2;
          numberS[1].value = n1;
          n1 = parseFloat(numberS[0].value);
          n2 = parseFloat(numberS[1].value);
        }
        rangeS[0].value = String(n1);
        rangeS[1].value = String(n2);
      };

      rangeS[0].oninput = syncRangeToNumbers;
      rangeS[1].oninput = syncRangeToNumbers;
      numberS[0].oninput = syncNumbersToRange;
      numberS[1].oninput = syncNumbersToRange;
    });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (!target) return;
      target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const fromFetch = html.querySelector(selector);
      const inDom = document.querySelector(selector);
      if (!fromFetch || !inDom) return;
      inDom.innerHTML = fromFetch.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm && mobileForm.closest('menu-drawer');
    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    const sectionId = productGrid?.dataset?.id || productGrid?.getAttribute('data-id');
    return [
      {
        section: sectionId,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    let searchParams =
      event.currentTarget.href.indexOf('?') === -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const sortForm = document.getElementById('FacetSortForm');
    if (sortForm) {
      const sp = new URLSearchParams(searchParams);
      if (!sp.has('sort_by')) {
        const sortBy = new FormData(sortForm).get('sort_by');
        if (sortBy) {
          sp.set('sort_by', sortBy);
          searchParams = sp.toString();
        }
      }
    }
    FacetFiltersForm.renderPage(searchParams, event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const filterForm = document.getElementById('FacetFiltersForm');
    const facetHost =
      (filterForm && filterForm.closest('facet-filters-form')) ||
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');
    if (facetHost && typeof facetHost.onActiveFilterClick === 'function') {
      facetHost.onActiveFilterClick(event);
    } else if (event.currentTarget.href) {
      window.location.assign(event.currentTarget.href);
    }
  }
}

customElements.define('facet-remove', FacetRemove);

function bootPriceRangeSliders() {
  FacetFiltersForm.initPriceRangeSliders();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootPriceRangeSliders);
} else {
  bootPriceRangeSliders();
}
